import { useCallback, useEffect, useMemo, useState } from 'react';

import { notification, TablePaginationConfig } from 'antd';

interface IListDataPagination {
  total: number;
  page: number;
  pageSize: number;
}

interface IListData<T> extends IListDataPagination {
  items: T[];
}

interface IListDataConfig<F> {
  defaultPageSize?: number;
  preventFirstLoad?: boolean;
  defaultFilters?: F;
}

export type HydraListResponse<T> = {
  'hydra:member': T[];
  'hydra:totalItems': number;
};

export type LoadListDataParams<F extends object = {}> = Omit<IListDataPagination, 'total'> & F;
export type LoadListDataResponse<T> = Pick<IListData<T>, 'items' | 'total'>;
export type LoadListData<T, F extends object> = (params: LoadListDataParams<F>) => Promise<LoadListDataResponse<T>>;

const useListData = <T, F extends object>(
  getItems: LoadListData<T, F>,
  { defaultPageSize = 10, preventFirstLoad = false, defaultFilters = {} as F }: IListDataConfig<F> = {},
) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<IListData<T>>({
    items: [],
    total: 0,
    page: 1,
    pageSize: 10,
  });
  const [filters, setFilters] = useState<F>(defaultFilters);

  const loadData = useCallback(
    async (data: { page?: number; pageSize?: number; filters?: F } = {}) => {
      const { page = 1, pageSize = defaultPageSize, filters = {} as F } = data;
      setLoading(true);
      try {
        const res = await getItems({ ...filters, page, pageSize });
        const { items, total } = res;
        setData({
          items,
          total,
          page,
          pageSize,
        });
      } catch (message: any) {
        notification.error({ message });
      } finally {
        setLoading(false);
      }
    },
    [getItems, defaultPageSize],
  );

  const handleTableChange = (pagination: TablePaginationConfig, newFilters: Partial<LoadListDataParams<F>>) => {
    setFilters({ ...filters, ...newFilters });
    return loadData({
      page: pagination.current,
      pageSize: pagination.pageSize,
      filters: { ...filters, ...newFilters },
    });
  };

  const handleFiltersChange = (values: F) => {
    let newFilters = { ...filters, ...values };
    newFilters = Object.entries(newFilters).reduce((result, [key, value]) => {
      if (value !== undefined) {
        return {
          ...result,
          [key]: typeof value === 'string' ? value.trim() : value,
        };
      }
      return result;
    }, {} as F);
    setFilters(newFilters);
    return loadData({ filters: newFilters });
  };

  useEffect(() => {
    !preventFirstLoad && loadData({ filters: defaultFilters });
  }, []);

  const pagination = useMemo(
    () => ({
      total: data.total,
      current: data.page,
      pageSize: data.pageSize,
    }),
    [data],
  );

  return {
    items: data.items,
    loadData,
    loading,
    filters,
    onFiltersChange: handleFiltersChange,
    onTableChange: handleTableChange,
    pagination,
  };
};

export default useListData;
